// A comment (design §7.3, rewritten for Phase 4.5).
//
// Shut, it is one sentence: what the advisor thinks. Open, it says why that
// matters for the goal, how to act on it, and what acting on it would look
// like — the four sentences the schema will not let it leave out.
//
// The example is shown and never inserted. There is no control that moves it
// into the document, and the copy button takes the whole comment rather than
// the example alone, because a button that lifted out a suggested sentence
// would be a paste button with a longer name (D31).

import { Card, Reveal } from "./Card";
import { type Comment, type Note, type SourceRef, asText, notesFor, sourcesFor } from "./types";
import { NoteBody } from "./NoteCard";
import { IconButton } from "../ui/TopBar";

const ICON: Record<Comment["type"], string> = {
  problem: "!",
  question: "?",
  idea: "+",
};

const LABEL: Record<Comment["type"], string> = {
  problem: "Problem",
  question: "Question",
  idea: "Idea",
};

interface Props {
  comment: Comment;
  notes: Note[];
  searchEnabled: boolean;
  reassessing: boolean;
  /** The advisor just judged the edit to have addressed it. */
  success: boolean;
  onDismiss: () => void;
  onReassess: () => void;
  onCopy: (text: string) => void;
  onEnableSearch: () => void;
}

export function CommentCard({
  comment,
  notes,
  searchEnabled,
  reassessing,
  success,
  onDismiss,
  onReassess,
  onCopy,
  onEnableSearch,
}: Props) {
  const sources = sourcesFor(comment, notes);
  const cited = notesFor(comment, notes);

  return (
    <Card
      kind="comment"
      className={`${comment.type}${success ? " success" : ""}`}
      badge={
        <>
          <span className={`kind ${comment.type}`} aria-hidden="true">
            {ICON[comment.type]}
          </span>
          <span className="kind-label">{LABEL[comment.type]}</span>
        </>
      }
      summary={withCitations(comment.what)}
      onDismiss={success ? undefined : onDismiss}
      actions={
        success ? (
          <p className="success-line">Success</p>
        ) : (
          <div className="advice-actions">
            <IconButton
              icon="copy"
              label="Copy this comment"
              size={22}
              onClick={() => onCopy(asText(comment))}
            />
            <IconButton
              icon="brain"
              label={reassessing ? "Reassessing" : "Reassess: did the edit address this?"}
              primary
              size={24}
              busy={reassessing}
              disabled={reassessing || comment.state !== "live"}
              onClick={onReassess}
            />
          </div>
        )
      }
    >
      <dl className="four">
        <dt>Why</dt>
        <dd>{withCitations(comment.why)}</dd>
        <dt>How</dt>
        <dd>{withCitations(comment.how)}</dd>
        <dt>For example</dt>
        <dd className="example">{withCitations(comment.example)}</dd>
      </dl>

      {sources.length > 0 && <SourceList sources={sources} />}

      {cited.length > 0 && (
        <Reveal label="Notes" count={cited.length}>
          {cited.map((note) => (
            <article key={note.slug} className="cited-note">
              <p className="claim">{note.claim}</p>
              <NoteBody note={note} />
            </article>
          ))}
        </Reveal>
      )}

      {comment.reasoning && (
        <Reveal label="Reasoning">
          <p className="reasoning">{withCitations(comment.reasoning)}</p>
        </Reveal>
      )}

      {comment.action === "enable_search" && !searchEnabled && (
        <button type="button" className="ask inline" onClick={onEnableSearch}>
          Let it check the web for this document
        </button>
      )}
    </Card>
  );
}

export function SourceList({ sources }: { sources: SourceRef[] }) {
  return (
    <ol className="sources">
      {sources.map((s) => (
        <li key={s.id}>
          <a href={s.url} target="_blank" rel="noreferrer noopener">
            {hostOf(s.url)}
          </a>
          <span className="muted small"> · read {when(s.retrieved_at)}</span>
          {s.stale && <span className="stale"> may be out of date</span>}
        </li>
      ))}
    </ol>
  );
}

/** [1] and [2] become small superscripts pointing at the sources below. */
export function withCitations(text: string) {
  const parts = text.split(/(\[\d+\])/g);
  return parts.map((part, i) => {
    const match = /^\[(\d+)\]$/.exec(part);
    return match ? (
      <sup key={i} className="citation">
        {match[1]}
      </sup>
    ) : (
      <span key={i}>{part}</span>
    );
  });
}

export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function when(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

export function sourceList(sources: SourceRef[]): string {
  return sources.map((s) => hostOf(s.url)).join(", ");
}
